import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/primitives';
import VideoPlayer from '../components/viewers/VideoViewer/VideoPlayer';
import ActionRow from '../components/viewers/VideoViewer/ActionRow';
import InfoPanel from '../components/viewers/VideoViewer/InfoPanel';
import DeleteConfirmModal from '../components/viewers/VideoViewer/DeleteConfirmModal';
import {
  VIEWER_ICON_BUTTON_STYLE,
  VIEWER_INLINE_CONTROL_INSET,
  VIEWER_TOP_CONTROL_OFFSET,
} from '../components/viewers/viewerLayout';
import {
  useDeleteGalleryItemMutation,
  usePrepareShareMutation,
} from '../core/queries/mutations';
import useGalleryItem from '../hooks/useGalleryItem';
import {
  downloadAsset,
  session,
  sharePreparedMessage,
  showAlert,
} from '../platform';
import useConsentGuard from '../hooks/useConsentGuard';

/**
 * VideoViewer — mirrors PhotoViewer layout, with VideoPlayer instead of image.
 *
 * Figma nodes:
 *  - Default:  319:5793
 *  - Info:     320:5894
 *  - Delete:   319:5871
 *
 * Layout (aligned with PhotoViewer):
 *  - root fills the dynamic viewport
 *  - video stage ends exactly where the fixed-height action panel begins
 *  - video uses cover; playback controls are pinned to the stage bottom
 *  - pinned 32×32 info button top-left; metadata chips slide out to its right
 *  - 32×32 remove button top-right (glass-12)
 *  - source photo references sit above the player controls inside the video overlay
 *  - bottom block contains only the compact ActionRow
 *
 * Route: /gallery/:id (type=video, mounted by GalleryItemViewer)
 * or directly by parent component passing item prop.
 *
 * Props:
 *  item?    — gallery item (if pre-fetched by GalleryItemViewer)
 *  id?      — gallery item id (if no item, fetches itself)
 */
export default function VideoViewer({ item: itemProp, id: idProp }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const itemId = idProp || routeId;

  const { item: fetchedItem, loading, error } = useGalleryItem(itemProp ? null : itemId);
  const item = itemProp || fetchedItem;
  const sourceReferenceUrls = Array.from(new Set([
    item?.srcImageUrl,
    item?.referenceImageUrl,
    ...(Array.isArray(item?.referenceImageUrls) ? item.referenceImageUrls : []),
  ].filter(Boolean)));

  const [showInfo, setShowInfo] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteGalleryItemMutation = useDeleteGalleryItemMutation();
  const prepareShareMutation = usePrepareShareMutation();

  // ---- handlers ----------------------------------------------------------------

  const handleInfo = useCallback(() => {
    setShowInfo((v) => !v);
  }, []);

  const handleDownload = useCallback(() => {
    if (!item?.videoUrl) return;
    if (!downloadAsset({ url: item.videoUrl, filename: `video-${item.id}.mp4` })) {
      const a = document.createElement('a');
      a.href = item.videoUrl;
      a.download = `video-${item.id}.mp4`;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
  }, [item]);

  const handleShare = useCallback(async () => {
    if (!item?.id || prepareShareMutation.isPending) return;
    try {
      const result = await prepareShareMutation.mutateAsync({
        generationId: item.id,
        language: i18n.resolvedLanguage || i18n.language,
      });
      if (!sharePreparedMessage(result?.preparedMessageId)) {
        throw new Error('Telegram shareMessage is unavailable');
      }
    } catch {
      showAlert(t('viewer.shareFailed'));
    }
  }, [i18n.language, i18n.resolvedLanguage, item?.id, prepareShareMutation, t]);

  const performRepeat = useCallback(() => {
    if (!item) return;
    if (!item.presetId) {
      navigate('/photo-catalog');
      return;
    }
    session.setItem('repeatVideoAspectRatio', item.aspectRatio || '9:16');
    session.setItem('repeatVideoQuality', item.quality || 'MQ');
    const repeatReference = item.srcImageUrl || item.referenceImageUrl || sourceReferenceUrls[0];
    if (repeatReference) {
      session.setItem('repeatVideoReference', repeatReference);
    }
    navigate(`/video-generate/${item.presetId}`);
  }, [item, navigate, sourceReferenceUrls]);
  const handleRepeat = useCallback(
    () => requireConsent(performRepeat, 'generate'),
    [performRepeat, requireConsent],
  );

  const handleDeleteRequest = useCallback(() => {
    setShowDeleteModal(true);
    setShowInfo(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteGalleryItemMutation.mutateAsync(itemId);
      navigate('/gallery');
    } catch (err) {
      setDeleteError(err?.response?.data?.error || t('viewer.deleteFailed'));
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }, [deleteGalleryItemMutation, itemId, navigate, t]);

  // ---- loading / error states --------------------------------------------------

  if (loading) {
    return (
      <div
        className="min-h-screen bg-bg-app flex items-center justify-center"
        data-testid="video-viewer-loading"
      >
        <Icon name="loader" size={32} className="text-text-secondary animate-spin" />
      </div>
    );
  }

  if (error || (!loading && !item)) {
    return (
      <div
        className="min-h-screen bg-bg-app flex flex-col items-center justify-center gap-4 px-6"
        data-testid="video-viewer-error"
      >
        <p className="font-sans text-text-secondary text-chip-label text-center">
          {error || t('viewer.videoNotFound')}
        </p>
        <button
          type="button"
          className="font-sans text-chip-label text-coin-yellow underline"
          onClick={() => navigate('/gallery')}
        >
          {t('viewer.backToGallery')}
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative h-[100dvh] overflow-hidden bg-bg-app"
      style={{
        '--video-action-panel-height': 'calc(72px + var(--safe-bottom, 0px))',
      }}
      data-testid="video-viewer"
    >
      {/* ── Video stage — fills everything above the action panel ───────────── */}
      <div
        className="absolute left-0 right-0 top-0 overflow-hidden bg-bg-app"
        style={{ bottom: 'var(--video-action-panel-height)' }}
        data-testid="video-container"
      >
        {item.videoUrl ? (
          <VideoPlayer
            src={item.videoUrl}
            className="absolute inset-0"
            referenceImageUrls={sourceReferenceUrls}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon name="video" size={48} className="text-text-secondary" />
          </div>
        )}

        {/* Top-left: pinned Info button, matching PhotoViewer. */}
        <button
          type="button"
          onClick={handleInfo}
          aria-label={t('common.info', 'Info')}
          aria-pressed={showInfo}
          className="absolute z-30 flex items-center justify-center rounded-home-16 text-text-primary"
          style={{
            ...VIEWER_ICON_BUTTON_STYLE,
            top: VIEWER_TOP_CONTROL_OFFSET,
            left: VIEWER_INLINE_CONTROL_INSET,
          }}
          data-testid="btn-info"
        >
          <Icon name="info" size={16} className="text-text-primary" />
        </button>

        {/* Top-right: Remove button 32×32 */}
        <button
          type="button"
          onClick={handleDeleteRequest}
          aria-label={t('common.delete')}
          className="absolute z-30 flex items-center justify-center rounded-home-16 text-text-primary"
          style={{
            ...VIEWER_ICON_BUTTON_STYLE,
            top: VIEWER_TOP_CONTROL_OFFSET,
            right: VIEWER_INLINE_CONTROL_INSET,
          }}
          data-testid="btn-remove"
        >
          <Icon name="delete" size={18} className="text-text-primary" />
        </button>

        {/* Info Panel overlay */}
        {showInfo && (
          <InfoPanel item={item} onClose={() => setShowInfo(false)} />
        )}

        {/* Delete Confirm Modal */}
        {showDeleteModal && (
          <DeleteConfirmModal
            onConfirm={handleDeleteConfirm}
            onCancel={() => setShowDeleteModal(false)}
            loading={deleting}
          />
        )}
      </div>

      {/* ── Bottom action panel — the video stage stops at this boundary ────── */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 bg-bg-app px-4 pt-2"
        style={{
          height: 'var(--video-action-panel-height)',
          paddingBottom: 'calc(16px + var(--safe-bottom, 0px))',
        }}
        data-testid="video-action-panel"
      >
        <div data-testid="action-row-wrapper">
          <ActionRow
            onDownload={handleDownload}
            onShare={handleShare}
            onRepeat={handleRepeat}
          />
        </div>
      </div>

      {deleteError && (
        <div
          className="absolute left-1/2 -translate-x-1/2 px-4 py-2 rounded-home-16 bg-surface-2 text-text-secondary font-sans"
          style={{
            bottom: 'calc(var(--video-action-panel-height) + 16px)',
            fontSize: 12,
            zIndex: 50,
            whiteSpace: 'nowrap',
          }}
          data-testid="delete-error"
        >
          {deleteError}
        </div>
      )}
    </div>
  );
}
